import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApnsConfiguration } from "@/types/apns";
import { getAllCustomers } from "@/utils/customerLookup";
import { useToast } from "@/hooks/use-toast";
import { HelpCircle, Upload, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import * as tenantsApi from "@/services/tenantsApi";

interface ApnsConfigurationDialogProps { open: boolean; onOpenChange: (open: boolean) => void; onSuccess: () => void; initialData?: ApnsConfiguration | null; }

export function ApnsConfigurationDialog({ open, onOpenChange, onSuccess, initialData }: ApnsConfigurationDialogProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [name, setName] = useState(initialData?.name || "");
  const [customerId, setCustomerId] = useState(initialData?.customer_id || "global");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(initialData?.email_address || "");
  const [certificateFile, setCertificateFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (file) setCertificateFile(file);
  };

  const handleUploadCertificate = async () => {
    if (!certificateFile) return;
    setIsUploading(true);
    try {
      await tenantsApi.uploadApnsCert(certificateFile, "");
      toast({ title: "Success", description: "APNs certificate uploaded successfully!" });
      onOpenChange(false); onSuccess(); resetForm();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to upload certificate", variant: "destructive" });
    } finally { setIsUploading(false); }
  };

  const resetForm = () => { setName(""); setCustomerId("global"); setDescription(""); setEmail(""); setCertificateFile(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initialData ? "Edit" : "Add New"} APNs Configuration</DialogTitle><DialogDescription>Configure Apple Push Notification service settings.</DialogDescription></DialogHeader>
        <TooltipProvider>
          <div className="space-y-4 pt-4">
            <div className="space-y-2"><Label>Configuration Name <span className="text-destructive">*</span></Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Production APNs" /></div>
            <div className="space-y-2"><Label>Assign to Customer</Label><Select value={customerId} onValueChange={setCustomerId}><SelectTrigger><SelectValue placeholder="Select a customer" /></SelectTrigger><SelectContent><SelectItem value="global">Global</SelectItem>{getAllCustomers().map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Email Address</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="apple.id@example.com" /></div>
            <Alert><HelpCircle className="h-4 w-4" /><AlertDescription><a href="https://identity.apple.com/pushcert/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Open Apple Push Certificates Portal<ExternalLink className="h-3 w-3" /></a></AlertDescription></Alert>
            <div className="space-y-2"><Label>Certificate File (.pem)</Label><Input type="file" accept=".pem" onChange={handleFileChange} /></div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="button" onClick={handleUploadCertificate} disabled={!certificateFile || isUploading} className="gap-2"><Upload className="h-4 w-4" />{isUploading ? "Uploading..." : "Upload Certificate"}</Button>
            </div>
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
